import type { DecipherDatamap, RespondentRecord, StudyFacts } from './report-aggregation';
import type { ReportDesign } from '../../models/end-client-reporting-package';
import { withCurrentReportTypes } from './report-type-aliases';

/**
 * Reads the frozen bundle a published, client-facing report is served, and turns it back into the
 * `RespondentRecord[]` the renderer already knows how to count (NK-399).
 *
 * The contract is `fixtures/index-format.md`; read it before changing anything here. The rule the
 * whole format is built around: **the viewer unpacks and then runs `report-aggregation.ts`
 * unchanged.** It does not count over masks. Two implementations of the same arithmetic — a row path
 * in the editor and a mask path here — is the failure being avoided, because the counts *are* the
 * product and a drift between them would put two different numbers on the same deck with nothing to
 * flag it.
 *
 * So nothing in this file aggregates, bases or averages. It decodes.
 */

export interface ReportBundleReport {
  name: string;
  description: string;
}

export interface ReportBundleOption {
  /** A datamap code for a coded variable; a column name for a multi-punch. */
  code: string;
  mask: string;
  /** Present in the data, absent from the datamap's values — `aggregate()`'s unknown-code path. */
  unmapped?: boolean;
}

export type ReportBundleVariable =
  | { kind: 'coded'; column: string; asked: string; options: ReportBundleOption[] }
  | { kind: 'multi'; asked: string; options: ReportBundleOption[] }
  | { kind: 'numeric'; asked: string; columns: string[] };

export interface ReportBundleSource {
  name: string;
  kind: number;
  fetchedAt: string | null;
  n: number;
  maskBytes: number;
  scalars: StudyFacts;
  variables: Record<string, ReportBundleVariable>;
  /**
   * Numeric values and open-end text, verbatim, null where the respondent has none. Always carries
   * `qtime`.
   *
   * One shape for both because they decode the same way -- straight into the record -- and an
   * open-end needs nothing else: there is no option list to enumerate and nothing to count, so it
   * has no `variables` entry at all and the datamap describes it.
   */
  columns: Record<string, (string | null)[]>;
}

export interface ReportBundle {
  $format: string;
  generatedAt?: string;
  report: ReportBundleReport;
  design: ReportDesign;
  datamap: DecipherDatamap;
  defaultSourceId: string;
  sources: Record<string, ReportBundleSource>;
}

export const REPORT_BUNDLE_FORMAT = 'navigator-endclientreport-bundle/1';

/**
 * base64 to bytes. `atob` rather than Node's `Buffer`: this is library code compiled without Node
 * types, and every runtime it actually runs in — the browser, jsdom under Jest, Node 18 and up —
 * has it globally.
 */
function decode(mask: string): Uint8Array {
  const binary = atob(mask);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** LSB-first within each byte, so the layout is endian-independent. */
function isSet(bytes: Uint8Array, respondent: number): boolean {
  return (bytes[respondent >> 3] & (1 << (respondent & 7))) !== 0;
}

/**
 * One source's respondents, in the order the pull stored them. Bit *i* and `columns[…][i]` are the
 * same respondent — an artefact of that file, never a stable id, so masks and columns from different
 * pulls must not be mixed.
 */
export function unpackSource(source: ReportBundleSource): RespondentRecord[] {
  const records: RespondentRecord[] = [];
  for (let i = 0; i < source.n; i++) records.push({});

  for (const variable of Object.values(source.variables ?? {})) {
    if (variable.kind === 'coded') {
      unpackCoded(variable, records);
    } else if (variable.kind === 'multi') {
      unpackMulti(variable, records);
    }
    // numeric carries no option masks: its values arrive through `columns` below, and a
    // present-but-unparseable cell comes back as the string it was.
  }

  for (const [column, values] of Object.entries(source.columns ?? {})) {
    values.forEach((value, i) => {
      if (value === null || value === undefined || !records[i]) return;
      records[i][column] = value;
    });
  }

  return records;
}

/**
 * Every code whose bit is set, not just the first. `codesOf()` accepts an array, so a coded column
 * may legitimately hold several codes — writing a scalar once per set bit would keep only the last,
 * dropping the rest and flipping `multiResponse` with them.
 */
function unpackCoded(
  variable: Extract<ReportBundleVariable, { kind: 'coded' }>,
  records: RespondentRecord[],
): void {
  const options = (variable.options ?? []).map(option => ({ code: option.code, mask: decode(option.mask) }));

  for (let i = 0; i < records.length; i++) {
    const codes = options.filter(option => isSet(option.mask, i)).map(option => option.code);
    if (!codes.length) continue;
    records[i][variable.column] = codes.length === 1 ? codes[0] : codes;
  }
}

/**
 * **The failure mode of this format.** Decipher writes `0` for an option shown and not picked, and
 * omits the columns entirely when the question was never shown; `baseFor()` reads presence, so the
 * two are not interchangeable. Leaving unpicked columns absent turns "saw it, picked nothing" into
 * "never saw it", which deflates the base and inflates every percentage computed against it.
 *
 * Hence: `asked` clear leaves every column absent, `asked` set writes every column.
 */
function unpackMulti(
  variable: Extract<ReportBundleVariable, { kind: 'multi' }>,
  records: RespondentRecord[],
): void {
  const asked = decode(variable.asked);
  const options = (variable.options ?? []).map(option => ({ column: option.code, mask: decode(option.mask) }));

  for (let i = 0; i < records.length; i++) {
    if (!isSet(asked, i)) continue;
    for (const option of options) {
      // The column's own name where selected: `isSelected()` is "not empty and not 0", so what
      // matters is which side of that line the value falls, and the name is self-describing in a
      // way "1" is not when someone opens the unpacked records in a debugger.
      records[i][option.column] = isSet(option.mask, i) ? option.column : '0';
    }
  }
}

export interface UnpackedBundle {
  report: ReportBundleReport;
  design: ReportDesign;
  datamap: DecipherDatamap;
  sourceId: string;
  source: ReportBundleSource;
  records: RespondentRecord[];
  /** Precomputed, because `studyFacts()` describes the study as fielded and never changes with the rail. */
  scalars: StudyFacts;
}

/**
 * The whole bundle, resolved to one source — `sourceId` when given, otherwise `defaultSourceId`.
 * Every mapped source is packed even while the viewer offers no way to switch, so turning that on
 * later needs no re-deploy.
 */
export function unpackBundle(bundle: ReportBundle, sourceId?: string): UnpackedBundle {
  if (bundle?.$format !== REPORT_BUNDLE_FORMAT) {
    throw new Error(`Unsupported report bundle format: ${bundle?.$format ?? 'missing'}`);
  }

  const id = sourceId && bundle.sources?.[sourceId] ? sourceId : bundle.defaultSourceId;
  const source = bundle.sources?.[id];
  if (!source) throw new Error(`The bundle carries no data source ${id}.`);

  return {
    report: bundle.report,
    // A bundle published before the modules were renamed keeps its old keys until it is republished.
    design: withCurrentReportTypes(bundle.design),
    datamap: bundle.datamap,
    sourceId: id,
    source,
    records: unpackSource(source),
    scalars: source.scalars,
  };
}
